import { useEffect, useState } from 'react'
import type { LucideProps } from 'lucide-react'

// `import { icons }` pulls all ~1,900 lucide modules (477 kB) into whatever
// chunk touches this file - it was landing in the entry bundle. The set cannot
// be narrowed to an allowlist because LucideIconPicker lets an admin choose any
// name and stores it in config, so instead the whole map loads as ONE lazy
// chunk, off the first-paint path. Callers render `fallback` until it arrives.
type IconComponent = React.ComponentType<Omit<LucideProps, 'ref'>>
type IconMap = Record<string, IconComponent>

// Module-level, so the index is built ONCE per page rather than once per
// component instance. buildIconIndex walks ~1,700 icons through three regexes,
// which is far too expensive to repeat for every rendered icon.
let loadedIndex: IconIndex | null = null
let pendingLoad: Promise<IconIndex> | null = null

function loadIconIndex(): Promise<IconIndex> {
  if (loadedIndex) return Promise.resolve(loadedIndex)
  // Deliberately the deep icons-index path, NOT `import('lucide-react')`: the
  // barrel is what every other module imports statically, so importing it here
  // makes Rollup merge the whole set into an EAGER shared chunk and the split
  // buys nothing. This module is reachable only from here, so it splits off.
  pendingLoad ??= import('lucide-react/dist/esm/icons/index.js').then((mod) => {
    loadedIndex = buildIconIndex(mod as unknown as IconMap)
    return loadedIndex
  })
  return pendingLoad
}

function pascalToKebab(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    // Digit boundary, so `Gamepad2` becomes `gamepad-2` like upstream rather
    // than `gamepad2`. Names are stored in tenant config, so spelling matters.
    .replace(/([a-z])([0-9])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()
}

interface IconIndex {
  /** Lookup keyed by kebab name, plus a hyphen-insensitive alias per icon. */
  byName: Map<string, IconComponent>
  /** Canonical names, every one guaranteed present in `byName`. */
  names: string[]
}

/**
 * Index the icon set by kebab name.
 *
 * Names are derived here and the picker lists exactly these keys, so "every
 * name offered resolves" holds by construction - no PascalCase round-trip to
 * drift. A hyphen-stripped alias is registered alongside each name because no
 * single regex reproduces upstream's spelling for every icon (`Grid2x2` is
 * `grid-2x2` upstream, not `grid-2x-2`), and because config written before the
 * digit rule holds the unseparated form. Any hyphenation variant resolves.
 */
function buildIconIndex(icons: IconMap): IconIndex {
  const byName = new Map<string, IconComponent>()
  const names: string[] = []
  for (const [pascal, component] of Object.entries(icons)) {
    const kebab = pascalToKebab(pascal)
    names.push(kebab)
    byName.set(kebab, component)
    const squashed = kebab.replace(/-/g, '')
    if (!byName.has(squashed)) byName.set(squashed, component)
  }
  return { byName, names: names.sort() }
}

function lookupIcon(index: IconIndex, name: string): IconComponent | undefined {
  return index.byName.get(name) ?? index.byName.get(name.replace(/-/g, ''))
}

/** The lazily-loaded icon index; `null` until the chunk resolves. */
// eslint-disable-next-line react-refresh/only-export-components
export function useLucideIconIndex(): IconIndex | null {
  const [index, setIndex] = useState<IconIndex | null>(loadedIndex)
  useEffect(() => {
    if (index) return
    let active = true
    void loadIconIndex().then((built) => {
      if (active) setIndex(built)
    })
    return () => {
      active = false
    }
  }, [index])
  return index
}

/** Every selectable icon name in kebab-case; empty until the chunk resolves. */
// eslint-disable-next-line react-refresh/only-export-components
export function useLucideIconNames(): string[] {
  const index = useLucideIconIndex()
  return index?.names ?? []
}

interface LucideIconByNameProps extends Omit<LucideProps, 'ref'> {
  name: string | null | undefined
  fallback?: React.ReactNode
}

export function LucideIconByName({ name, fallback = null, ...props }: LucideIconByNameProps) {
  const index = useLucideIconIndex()
  // Covers all three "nothing to draw" cases: no name, set still loading, and a
  // name that is not an icon. Callers used to pre-check with a synchronous
  // isValidLucideIcon(); that cannot work against a lazy set, so validity is
  // resolved here and surfaced through `fallback`.
  if (!name || !index) return <>{fallback}</>
  const Icon = lookupIcon(index, name)
  if (!Icon) return <>{fallback}</>
  // lookupIcon reads an existing component out of a Map rather than creating
  // one, so identity is stable; the lint rule cannot see that through the call.
  // eslint-disable-next-line react-hooks/static-components
  return <Icon {...props} />
}
